
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Shuffle } from "lucide-react";
import { Topic } from "@/types/topic";
import { Separator } from "@/components/ui/separator";

interface TopicSelectorProps {
    selectedTopicName: string | null;
    onTopicSelect: (topicName: string) => void;
    onQuestionSelect: (questionId: string) => void;
    onRandomQuestion: () => void;
    topics: Topic[];
    currentQuestion?: Topic;
    availableTopics: string[];
}

export const TopicSelector = ({
    selectedTopicName,
    onTopicSelect,
    onQuestionSelect,
    onRandomQuestion,
    topics,
    currentQuestion,
    availableTopics,
}: TopicSelectorProps) => {
    const currentTopicQuestions = topics.filter(t => t.topic === selectedTopicName);
    const currentQuestionIndex = currentQuestion
        ? currentTopicQuestions.findIndex(q => q.id === currentQuestion.id)
        : -1;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 topic-selector">
            <div className="space-y-5">
                <div className="space-y-1">
                    <label className="text-sm font-medium text-stone-500 dark:text-stone-500">Topic</label>
                    <Select value={selectedTopicName || ''} onValueChange={onTopicSelect}>
                        <SelectTrigger data-topic-selector="true" className="dark:bg-stone-800 dark:text-white dark:border-stone-700">
                            <SelectValue placeholder="Choose a topic" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-stone-800 dark:border-stone-700">
                            {availableTopics.map((topic) => (
                                <SelectItem key={topic} value={topic} className="dark:text-white dark:focus:bg-stone-700 dark:data-[highlighted]:bg-stone-700">
                                    {topic}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {selectedTopicName && (
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-stone-500 dark:text-stone-500">Question</label>
                        <div className="flex items-center justify-between gap-4">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                    const prevQuestion = currentTopicQuestions[currentQuestionIndex - 1];
                                    if (prevQuestion) onQuestionSelect(prevQuestion.id);
                                }}
                                disabled={currentQuestionIndex <= 0}
                                className="dark:bg-stone-800 dark:hover:bg-stone-700 dark:border-stone-700"
                            >
                                <ChevronLeft className="h-4 w-4 dark:text-white" />
                            </Button>

                            <Card className="flex-1 flex items-center justify-center h-10 bg-white border border-stone-200 rounded-lg shadow-none dark:bg-stone-800 dark:border-stone-700">
                                <CardTitle className="font-medium text-sm text-stone-900 dark:text-white">
                                    Question {currentQuestionIndex + 1} of {currentTopicQuestions.length}
                                </CardTitle>
                            </Card>

                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                    const nextQuestion = currentTopicQuestions[currentQuestionIndex + 1];
                                    if (nextQuestion) onQuestionSelect(nextQuestion.id);
                                }}
                                disabled={currentQuestionIndex >= currentTopicQuestions.length - 1}
                                className="dark:bg-stone-800 dark:hover:bg-stone-700 dark:border-stone-700"
                            >
                                <ChevronRight className="h-4 w-4 dark:text-white" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-6">
                <div className="flex flex-col items-center justify-between h-full">
                    <div className="h-1/3">
                        <Separator orientation="vertical" className="h-full dark:bg-stone-700" />
                    </div>
                    <span className="text-gray-500 dark:text-stone-500">or</span>
                    <div className="h-1/3">
                        <Separator orientation="vertical" className="h-full dark:bg-stone-700" />
                    </div>
                </div>
                <Button
                    variant="link"
                    onClick={onRandomQuestion}
                    className="p-0 text-orange-500 dark:text-orange-500 hover:no-underline"
                >
                    <Shuffle />
                    Get A Random Question
                </Button>
            </div>
        </div>
    );
};
